/**
 * Global Error Handler
 *
 * Provides a way to emit errors from non-React code (like axios interceptors)
 * that can be caught and displayed by React components using toast notifications.
 */

export type GlobalError = {
  title: string;
  message: string;
  type: 'error' | 'warning' | 'info';
  autoDisappear?: number;
};

type ErrorListener = (error: GlobalError) => void;

class GlobalErrorHandler {
  private listeners: ErrorListener[] = [];

  subscribe(listener: ErrorListener): () => void {
    this.listeners.push(listener);
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  emit(error: GlobalError): void {
    this.listeners.forEach(listener => {
      try {
        listener(error);
      } catch (err) {
        console.error('Error in global error listener:', err);
      }
    });
  }

  // Convenience methods
  emitError(title: string, message: string, autoDisappear?: number): void {
    this.emit({ title, message, type: 'error', autoDisappear });
  }

  emitWarning(title: string, message: string, autoDisappear?: number): void {
    this.emit({ title, message, type: 'warning', autoDisappear });
  }

  emitInfo(title: string, message: string, autoDisappear?: number): void {
    this.emit({ title, message, type: 'info', autoDisappear });
  }
}

export const globalErrorHandler = new GlobalErrorHandler();
